import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { Link, useSearch } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import Label from "components/ui/input/Label";
import { betterAuthErrorText, signInWithProvider } from "lib/auth/authClient";
import { newPasswordFieldRules } from "lib/auth/passwordRules";
import { useAwsWafWarmup } from "lib/auth/useAwsWafWarmup";
import { isPersonalEmailDomain } from "lib/emailUtils";
import { useSignUp } from "lib/hooks/api/auth/useSignUp";
import { sendGa4Event } from "../../lib/ga4Tracking";
import { Field, Form } from "./authPageStyles";
import { AuthSplitLayout } from "./AuthSplitLayout";
import { CheckYourEmailCard } from "./CheckYourEmailCard";
import { ExternalSignInButtons } from "./ExternalSignInButtons";
import { PasswordStrengthMeter } from "./PasswordStrengthMeter";
import { buildSignupContext } from "./tracking";

const SIGNUP_FORM = { id: "signup-form", name: "Signup" };

// Codes with their own message + link below, so the generic fallback skips them.
const SIGN_UP_ERRORS_WITH_OWN_COPY = ["SSO_REQUIRED", "LOGIN_METHOD_NOT_ALLOWED"];

type FormValues = {
  name: string;
  email: string;
  password: string;
};

// Email/password registration page wired to the auth-service (better-auth), served at /auth/register.
export function BetterAuthRegisterPage() {
  // Post-signup destination, email pre-fill, and marketing attribution, sanitised by the route's
  // validateSignupSearch. `strict: false` lets them resolve to undefined when rendered outside the
  // route (e.g. tests).
  const search = useSearch({ strict: false });
  const { redirect, email } = search;
  const warmWaf = useAwsWafWarmup();
  const {
    control,
    handleSubmit,
    getValues,
    watch,
    formState: { isDirty, isValid, errors },
  } = useForm<FormValues>({
    // Validate as the user types so `isValid` (which gates the submit button below) stays live and
    // the confirm-password match is checked without any manual trigger wiring.
    mode: "onChange",
    defaultValues: {
      name: "",
      email: email ?? "",
      password: "",
    },
  });

  // Where a social sign-in has got to. "starting" keeps the buttons disabled until the browser
  // leaves, and stays set on success; "failed" is a hand-off that never reached the provider (see
  // signInWithProvider), which would otherwise leave the button looking dead.
  const [socialStatus, setSocialStatus] = useState<"idle" | "starting" | "failed">("idle");

  const password = watch("password");
  const emailValue = watch("email");
  // The "account created" event + conversion pixels deliberately do NOT fire here: a signup isn't
  // real until the email is verified, and this path misses social / enterprise SSO. They fire once
  // the user is authenticated, from CurrentUserConfiguredProviders, covering all signup methods.
  const { mutate: signUp, isSuccess, isError, error, isLoading } = useSignUp();

  // GA4/GTM signup-form events. `form_start` fires once on first interaction.
  useEffect(() => {
    if (isDirty) {
      sendGa4Event({ event: "signup_form_start", form: SIGNUP_FORM });
    }
  }, [isDirty]);

  // `form_interaction` the first time each tracked field goes from empty to filled (name,
  // companyName, email only — never password).
  const previousFormValue = useRef<Partial<FormValues>>({});
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "name" || name === "email") {
        if (!previousFormValue.current[name] && value[name]) {
          sendGa4Event({
            event: "signup_form_interaction",
            form: { ...SIGNUP_FORM, fieldName: name },
          });
        }
      }
      previousFormValue.current = value;
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = handleSubmit((vals) => {
    // GA4/GTM: email_submitted + continue_click fire together on submit. signupContext carries
    // UTM/SEO into crm_data. No Amplitude identify here — this page fires no Amplitude events;
    // identity is set post-auth by tracking.init.
    sendGa4Event({ event: "email_submitted", form: SIGNUP_FORM });
    sendGa4Event({ event: "continue_click", form: SIGNUP_FORM });
    signUp({
      name: vals.name,
      email: vals.email,
      password: vals.password,
      redirect,
      signupContext: buildSignupContext(search),
    });
  });

  // requireEmailVerification is on server-side, so a successful sign-up sends a verification
  // email rather than signing the user in. Show a confirmation state instead of redirecting.
  // The same card serves the social path, which the auth-service redirects to /auth/verify-email.
  if (isSuccess) {
    return <CheckYourEmailCard email={getValues("email")} redirect={redirect} />;
  }

  return (
    <AuthSplitLayout>
      <Column>
        <Flex column gap={8}>
          <Text h1>Sign up for free</Text>
          <Text color="sysTextSecondary" fontWeight="500">
            Get started in minutes, no credit card required
          </Text>
        </Flex>
        <ExternalSignInButtons
          mode="register"
          disabled={socialStatus === "starting"}
          onSelect={async (provider) => {
            setSocialStatus("starting");
            try {
              await signInWithProvider(
                provider,
                redirect,
                buildSignupContext(search),
                getValues("email"),
              );
            } catch {
              setSocialStatus("failed");
            }
          }}
        />
        {socialStatus === "failed" && (
          <Text color="sysDestructiveDefault">
            We couldn&apos;t reach your sign-in provider. Please try again.
          </Text>
        )}
        <Form onSubmit={onSubmit} onFocusCapture={warmWaf}>
          <Field>
            <Label htmlFor="email">Work email</Label>
            <Input
              name="email"
              control={control}
              type="email"
              placeholder="name@email.com"
              rules={{
                required: { value: true, message: "Email is required" },
                validate: (value) => {
                  if (isPersonalEmailDomain(value)) {
                    return "Please provide a work email e.g. name@company.com";
                  }
                  return true;
                },
              }}
            />
            {errors.email?.message && (
              <Text color="sysDestructiveDefault">{errors.email.message}</Text>
            )}
          </Field>
          <Field>
            <Label htmlFor="name">Full name</Label>
            <Input
              name="name"
              control={control}
              type="text"
              placeholder="Jane Smith"
              rules={{
                required: { value: true, message: "Name is required" },
                validate: (value) => value.trim().length > 0 || "Name is required",
              }}
            />
            {errors.name?.message && (
              <Text color="sysDestructiveDefault">{errors.name.message}</Text>
            )}
          </Field>
          <Field>
            <Label htmlFor="password">Password</Label>
            <Input
              name="password"
              control={control}
              type="password"
              autoComplete="new-password"
              placeholder="Create a password"
              rules={newPasswordFieldRules}
            />
            {errors.password?.message && (
              <Text color="sysDestructiveDefault">{errors.password.message}</Text>
            )}
          </Field>
          <PasswordStrengthMeter password={password} />

          {isError && error?.code === "SSO_REQUIRED" && (
            <Text color="sysDestructiveDefault">
              Your company uses single sign-on (SSO).{" "}
              <Link to="/auth/login" search={{ email: getValues("email") }}>
                Log in with SSO
              </Link>{" "}
              instead.
            </Text>
          )}
          {isError && error?.code === "LOGIN_METHOD_NOT_ALLOWED" && (
            <Text color="sysDestructiveDefault">
              {betterAuthErrorText(error, "Sign up failed")}{" "}
              <Link to="/auth/login" search={{ email: getValues("email") }}>
                Go to log in
              </Link>{" "}
              to see the methods your company allows.
            </Text>
          )}
          {isError && !SIGN_UP_ERRORS_WITH_OWN_COPY.includes(error?.code ?? "") && (
            <Text color="sysDestructiveDefault">
              {betterAuthErrorText(error, "Sign up failed")}
            </Text>
          )}

          <Button
            buttonType="primary"
            type="submit"
            fullWidth
            loading={isLoading}
            disabled={!isValid}
          >
            Continue
          </Button>
        </Form>

        <Text color="sysTextSecondary">
          Already have an account?{" "}
          <Link to="/auth/login" search={{ email: emailValue || undefined }}>
            Log in
          </Link>
        </Text>

        <Text description color="sysTextSecondary">
          By signing up, you agree to Stotles&apos;{" "}
          <a href="https://www.stotles.com/terms-and-conditions" target="_blank" rel="noreferrer">
            Terms and Conditions
          </a>{" "}
          and{" "}
          <a href="https://www.stotles.com/privacy-policy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>
          .
        </Text>
      </Column>
    </AuthSplitLayout>
  );
}

// 32px between blocks, per the design — wider than the 16px the single-column pages use, because
// this column carries more of them. The heading and its supporting line stay a tight pair at 8px.
const Column = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 32,
  // Inline links, matched to the single-column cards (see authPageStyles' Card).
  a: {
    color: theme.colors.sysPrimaryDefault,
    textDecoration: "underline",
    textUnderlineOffset: 2,
    "&:hover": { color: theme.colors.sysPrimaryHover },
    "&:active": { color: theme.colors.sysPrimaryPressed },
  },
}));
